import React from "react";
import { useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../../store/store";
import { Badge, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import Swal from "sweetalert2";
import { clearUnsavedChanges } from "../../../store/slice";
import { confirmLeaveWithoutSaving } from "../../../helpers";
import { useFormStatusHook } from "../../../shared/hooks/useFormStatus.hook";
import { WebAppRole } from "../../../shared/types";

type Props = {
  link: string;
  muiIcon: React.ReactNode;
  handleNavLinkClick: () => void;
  label: string;
  /** Optional numeric badge (e.g. pending acks). 0/undefined hides it. */
  badgeCount?: number;
  /**
   * Si esta entrada es la activa. **Lo decide quien pinta la barra**, no la entrada.
   *
   * 🚨 Antes lo decidía ella con `pathname.startsWith(link)`, y con eso **se encendían dos a la
   * vez** siempre que una ruta fuera prefijo de otra: en `/dashboard/documentation/compliance`
   * se marcaban «Documentación» y «Documentación — control de lectura». *(Javier lo vio el
   * 2026-09-12 con «Sync QVET» y «Almacenes» encendidas juntas: «está como mezclado el menú con
   * los paths».)* La barra elige **la coincidencia más larga**, que es la entrada de verdad.
   */
  isActive?: boolean;
  /** Color of the badge dot. Default 'warning'. */
  badgeColor?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  /**
   * El color del grupo al que pertenece la entrada. Tiñe su icono, que es lo que hace que la
   * barra se lea como bloques y no como una lista larga.
   *
   * 🚨 **Se quita cuando la entrada está seleccionada.** El estado activo pinta el botón entero
   * de azul con texto blanco; dejar el icono en el color del grupo lo dejaría teñido sobre el
   * azul, que es justo donde peor se ve.
   */
  accentColor?: string;
};

export const SidebarLink: React.FC<Props> = ({
  link,
  muiIcon,
  handleNavLinkClick,
  label,
  badgeCount,
  badgeColor = 'warning',
  accentColor,
  isActive: isActiveProp,
}) => {
  const { menuState, isSmallScreen } = useSelector((state: RootState) => state.dbUi);
  const location = useLocation();
  // Sin el dato de quien pinta la barra, se cae al comportamiento de antes — que es correcto
  // mientras nadie más comparta prefijo.
  const isActive = isActiveProp ?? location.pathname.startsWith(link);

  const navigate = useNavigate();
  const { handleUnsavedChanges } = useFormStatusHook();

  const onClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    await handleUnsavedChanges(
      () => {
        navigate(link);
        handleNavLinkClick();
      },
      () => {}
    );
  };

  return (
    <ListItemButton
      component={NavLink}
      to={link}
      onClick={onClick}
      selected={isActive}
      sx={{
        alignItems: "center",
        padding: 0,
        paddingX: isSmallScreen ? 1 : 1,
        paddingY: isSmallScreen ? 0.5 : 0,
        minHeight: isSmallScreen ? 44 : 'auto',
        borderRadius: 2,
        ...(accentColor && {
          "& .MuiListItemIcon-root": { color: accentColor },
        }),
        "&.Mui-selected": {
          backgroundColor: "primary.main",
          color: "primary.contrastText",
          // El icono hereda el blanco del botón: sobre el relleno azul, el color del grupo
          // quedaría teñido y casi ilegible.
          "& .MuiListItemIcon-root": { color: "inherit" },
          "&:hover": {
            backgroundColor: "primary.dark",
          },
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: menuState !== "minimized" ? (isSmallScreen ? 1.5 : 1) : 0,
          '& svg': {
            fontSize: isSmallScreen ? '2.2rem' : '2rem',
          }
        }}
      >
        {badgeCount && badgeCount > 0 ? (
          <Badge badgeContent={badgeCount} color={badgeColor} max={99}>
            {muiIcon}
          </Badge>
        ) : (
          muiIcon
        )}
      </ListItemIcon>
      {menuState !== "minimized" && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: isSmallScreen ? "1.3rem" : "1.1rem",
            fontWeight: 500,
          }}
        />
      )}
    </ListItemButton>
  );
};
